// Canonical pt-BR formatters (Phase 1 SSOT).
//
// Use these instead of re-declaring `new Intl.NumberFormat("pt-BR", ...)` or
// `.toLocaleDateString("pt-BR")` inside components — there were ~25 local
// copies, several subtly different. Pick the variant that matches the intended
// display (e.g. `formatBRLInteger` for compact dashboards that omit cents).

export { formatBRL, formatBRLInteger, formatBrDate } from '@motor/formato.ts';

const BRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

/**
 * Live-typing BRL mask for controlled inputs: pass the raw typed string, get
 * back "R$ 1.234,56" (or "" for empty). Round-trips through `parseBRLInput`
 * on each keystroke — same pattern as the "Renda Mensal" field.
 */
export function formatBRLInput(value: number | string | null | undefined): string {
  if (typeof value === "string") return value;
  if (value == null || value === 0) return "";
  return BRL.format(value);
}

/** Inverse of `formatBRLInput` — strips "R$"/spaces/thousands dots, swaps the
 *  decimal comma for a dot, and parses. Invalid/empty input → null. */
export function parseBRLInput(value: string): number | null {
  if (!value) return null;
  const cleanValue = value.replace(/[R$\s.]/g, "").replace(",", ".");
  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Bank-app-style live mask: reads every digit in the raw input (ignoring
 * "R$", separators, cursor position — anything non-digit) and treats the
 * whole digit run as cents, so new digits shift the amount left as you type
 * (type "1","0","0","0","0","0" → "R$ 1.000,00"). Use this instead of
 * `parseBRLInput` for continuously-typed live masks: reformatting a fixed
 * "X,XX" string on every keystroke (as `parseBRLInput` expects) only ever
 * edits the last 2 decimal digits — appending more digits after the comma
 * gets rounded away instead of growing the amount. Empty/no digits → null.
 */
export function parseBRLCentsInput(value: string): number | null {
  const digits = value.replace(/\D/g, "");
  if (!digits) return null;
  return parseInt(digits, 10) / 100;
}

const MONTHS_ABBR = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

/** "2026-06" (or "2026-06-..") → "Jun/26". Unparseable input → returned as-is. */
export function formatMonthLabel(yyyymm: string): string {
  const m = yyyymm.match(/^(\d{4})-(\d{2})/);
  if (!m) return yyyymm;
  return `${MONTHS_ABBR[Number(m[2]) - 1]}/${m[1].slice(2)}`;
}


/** "dd/mm/yyyy" no fuso local — com uma blindagem que a versão anterior não
 *  tinha: data SEM hora ("YYYY-MM-DD") é um dia de calendário, não um
 *  instante. `new Date("1973-12-25")` parseia como meia-noite UTC e, em
 *  qualquer fuso negativo, vira 24/12 — foi exatamente o bug da data de
 *  nascimento na ficha de contato (a edição mostrava o valor certo, a ficha
 *  o dia anterior). A doc dizia "não use com ISO puro", mas doc não impede
 *  chamada errada; a função agora aceita os dois sem deslocar dia. */
export function formatBrDateLocal(value: string | number | Date): string {
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  return new Date(value).toLocaleDateString("pt-BR");
}

/** `new Date(value).toLocaleString("pt-BR")` → "dd/mm/yyyy hh:mm:ss" (local tz). */
export function formatBrDateTime(value: string | number | Date): string {
  return new Date(value).toLocaleString("pt-BR");
}
